import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Briefcase, ChevronDown, MapPin } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InlineEdit } from "@/components/InlineEdit";
import { AddContactModal } from "@/components/add/AddContactModal";
import { DocumentManager } from "@/components/DocumentManager";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import type { LogOption } from "@/components/activity/logOptions";
import { SourceCard } from "@/components/origination/SourceCard";
import { ProjectWizard } from "@/components/projects/ProjectWizard";
import { LinkProjectDialog } from "@/components/projects/LinkProjectDialog";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { TrackerHeadline } from "@/components/tracker/TrackerHeadline";
import { BuyerTrackerGrid } from "@/components/tracker/BuyerTrackerGrid";
import { AddBuyersDialog } from "@/components/tracker/AddBuyersDialog";
import { CloseDealDialog, ClosedSummary } from "@/components/clients/CloseDealDialog";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip, type SublineItem } from "@/components/record/RecordHeader";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { EditableTextPanel } from "@/components/record/EditableTextPanel";
import { CompanyRail } from "@/components/record/CompanyRail";
import { CompanyReach } from "@/components/record/CompanyReach";
import { companyLinkItems } from "@/components/record/LinkField";
import { CompanyNameEdit } from "@/components/record/CompanyNameEdit";
import { CompanyMoreMenu } from "@/components/record/CompanyMoreMenu";
import { ArchiveCompanyDialog } from "@/components/record/ArchiveCompanyDialog";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { clientFacts } from "@/components/record/facts/clientFacts";
import { fullDay, primaryContact, sourceLabel } from "@/components/record/recordFormat";
import { useAuth } from "@/lib/auth";
import { useCompanyField } from "@/hooks/useCompanyField";
import { useCompanySummary } from "@/hooks/useRecordSummary";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { useClientProject, useReactivateClient } from "@/hooks/useProjects";
import { useBuyerTracker, useOffers, type TrackerQuery } from "@/hooks/useBuyerTracker";
import { useClosing, useClosingMutations } from "@/hooks/useClosing";
import { useFeeDetail } from "@/hooks/useFinancials";
import { CLIENT_STAGES, CLIENT_STATUSES } from "@shared/clientConstants";
import { bestOffer, offerLines } from "@shared/offerMath";
import { todayKey } from "@shared/weeks";
import { ENGAGEMENT_TYPES, INDUSTRY_OPTIONS, type ClientData } from "./client/clientTypes";
import { ProcessPanel } from "./client/ProcessPanel";
import { DealSnapshotPanel } from "./client/DealSnapshotPanel";
import { HoldBanner, HoldClientDialog } from "./client/ClientHold";

type ClientTab = "overview" | "pipeline" | "documents";

// Only the funnel totals are read here (the Go-to-Market grid fetches its own pages).
const TRACKER_TOTALS: TrackerQuery = { page: 1, pageSize: 1, sort: "created", order: "desc", search: "", status: "all", milestones: [] };

const STATUS_TONE: Record<string, "good" | "warn" | "plain"> = { Active: "good", "On Hold": "warn", Closed: "plain" };
const TAB = "rounded-none border-b-2 border-transparent bg-transparent px-3 pb-2 pt-1 shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none";

/** A header chip that opens a menu of values (status, stage). */
function ChipMenu({ label, options, onPick, children }: { label: string; options: readonly string[]; onPick: (v: string) => void; children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={label} className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">{children}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((o) => <DropdownMenuItem key={o} onSelect={() => onPick(o)}>{o}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The Client page on the record frame. The header, facts and action bar sit
 * above three tabs. Overview holds the work column (brief, process, deal
 * snapshot, about, activity) and the company rail. Go-to-Market and Documents
 * render as they always have.
 */
export default function ClientRecord({ company }: { company: ClientData }) {
  const queryClient = useQueryClient();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const userTz = useCurrentUserTimezone();
  const { canViewFinancials } = useAuth();
  const { saveField, saveFieldAsync } = useCompanyField(company.id);
  const summary = useCompanySummary(company.id);
  const project = useClientProject(company.id);
  const tracker = useBuyerTracker(company.id, TRACKER_TOTALS);
  const offers = useOffers(company.id);
  const feeDetail = useFeeDetail(company.id, canViewFinancials);
  const closingQ = useClosing(company.id);
  const { reopen } = useClosingMutations(company.id);
  const reactivate = useReactivateClient();
  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();
  const [tab, setTab] = useState<ClientTab>("overview");
  const [holdOpen, setHoldOpen] = useState(false);
  const [closeDealOpen, setCloseDealOpen] = useState(false);
  const [projectWizardOpen, setProjectWizardOpen] = useState(false);
  const [linkProjectOpen, setLinkProjectOpen] = useState(false);
  const [changeProjectOpen, setChangeProjectOpen] = useState(false);
  const [addBuyersOpen, setAddBuyersOpen] = useState(false);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Pull this client's mail on open. The server rate-limits per company; a
  // missing Graph token fails quietly.
  useEffect(() => {
    fetch(`/api/emails/sync-company/${company.id}`, { method: "POST", credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.synced > 0) {
          queryClient.invalidateQueries({ queryKey: ["company-emails", company.id] });
          queryClient.invalidateQueries({ queryKey: ["record-summary", "company", company.id] });
        }
      })
      .catch(() => {});
  }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A prospect just converted lands here with ?promptProject=true: offer to create its project.
  useEffect(() => {
    if (search.includes("promptProject=true") && project.isSuccess && !project.data) {
      setProjectWizardOpen(true);
      setLocation(`/clients/${company.id}`, { replace: true });
    }
  }, [search, project.isSuccess, project.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // The log form and the brief live on Overview, so the action bar (and the L key) bring it forward first.
  const onLog = useCallback((type: LogOption) => { setTab("overview"); openLog(type); }, [openLog]);
  const onNextStep = () => { setTab("overview"); setNextStepOpen(true); };

  const people = company.contacts ?? [];
  const primary = primaryContact(people);
  const today = todayKey(new Date());
  const status = company.status || "Active";
  const isOnHold = company.status === "On Hold";
  const isClosed = company.status === "Closed";
  const closing = closingQ.data;
  const lines = offerLines(offers.data);
  const trackerTotal = tracker.data?.aggregates.total ?? 0;

  // "On Hold" goes through the hold dialog (reason and revisit date). "Closed"
  // goes through the close-deal dialog: closing is an event with terms, and the
  // dialog is what keeps a Closed status from existing with no closing record
  // (the state Ellis and Diversified are in).
  const handleStatusChange = (newStatus: string) => {
    if (newStatus === "On Hold") { setHoldOpen(true); return; }
    if (newStatus === "Closed") { setCloseDealOpen(true); return; }
    saveField("status", newStatus);
  };

  // Reopening drops the closing row, so confirm and name what is discarded.
  const handleReopen = () => {
    if (!closing) {
      saveField("status", "Active");
      return;
    }
    const what = closing.outcome === "Sold"
      ? `the recorded close (${closing.closed_on}, ${closing.acquirer_company_name || closing.acquirer_name || "acquirer on file"})`
      : `the recorded ${closing.outcome} outcome`;
    if (!window.confirm(`Reopen this deal? That deletes ${what} and returns the client to Active.`)) return;
    reopen.mutate(undefined, {
      onSuccess: () => toast.success("Deal reopened"),
      onError: (e: any) => toast.error(String(e?.message ?? e)),
    });
  };

  const onReactivate = () => {
    reactivate.mutate(company.id, {
      onSuccess: () => {
        toast.success("Client reactivated");
        queryClient.invalidateQueries({ queryKey: ["company", company.id] });
      },
      onError: () => toast.error("Failed to reactivate"),
    });
  };

  const subline: SublineItem[] = [
    { icon: <Briefcase />, content: <InlineEdit label="Industry" value={company.industry} type="select" options={INDUSTRY_OPTIONS} addLabel="Add industry" onSave={(v) => saveFieldAsync("industry", v)} /> },
    { icon: <MapPin />, content: <InlineEdit label="Location" value={company.headquarters} placeholder="City, State" addLabel="Add location" onSave={(v) => saveFieldAsync("headquarters", v)} /> },
    ...companyLinkItems({ website: company.website, linkedinUrl: company.linkedinUrl, onSave: (field, v) => saveFieldAsync(field, v) }),
  ];

  const header = (
    <RecordHeader
      crumb={{ label: "Clients", href: "/clients" }}
      current={company.name}
      name={<CompanyNameEdit value={company.name} onSave={(t) => saveFieldAsync("name", t)} />}
      chips={<>
        <RecordChip tone="category">Client</RecordChip>
        <ChipMenu label={`Status: ${status}`} options={CLIENT_STATUSES} onPick={handleStatusChange}>
          <RecordChip tone={STATUS_TONE[status] ?? "plain"}>{status}<ChevronDown className="h-3 w-3" /></RecordChip>
        </ChipMenu>
        <ChipMenu label={`Stage: ${company.stage || "not set"}`} options={CLIENT_STAGES} onPick={(s) => saveField("stage", s)}>
          <RecordChip>{company.stage || "Set stage"}<ChevronDown className="h-3 w-3" /></RecordChip>
        </ChipMenu>
        {company.engagementType && <RecordChip>{company.engagementType}</RecordChip>}
        {people.some((p) => p.engaged) && <RecordChip tone="good">Spoken with</RecordChip>}
      </>}
      subline={subline}
      reach={<CompanyReach people={people} mainPhone={company.enrichmentData?.phone} />}
      actions={
        <ActionBar onLog={onLog} email={primary?.email} onNextStep={onNextStep}>
          <EntityRemindersButton entityType="company" entityId={company.id} entityName={company.name} userTimezone={userTz} />
          <CompanyMoreMenu company={company} onArchive={() => setArchiveOpen(true)}>
            {isOnHold && <DropdownMenuItem disabled={reactivate.isPending} onSelect={onReactivate}>Reactivate</DropdownMenuItem>}
            {!isOnHold && !isClosed && <DropdownMenuItem onSelect={() => setHoldOpen(true)}>Put on hold</DropdownMenuItem>}
          </CompanyMoreMenu>
        </ActionBar>
      }
    />
  );

  const factsLoading = project.isLoading || tracker.isLoading || offers.isLoading || closingQ.isLoading || (canViewFinancials && feeDetail.isLoading);
  const facts = factsLoading ? (
    <div className="h-[58px] animate-pulse rounded-lg border bg-card" />
  ) : (
    <FactsStrip
      facts={clientFacts({
        today,
        status: company.status,
        project: project.data ? { phases: project.data.phases, milestones: project.data.milestones } : null,
        aggregates: tracker.data?.aggregates ?? null,
        best: bestOffer(lines),
        expectedCloseOn: company.expectedCloseOn ?? null,
        closing: closing ? { outcome: closing.outcome, closedOn: closing.closed_on, feeCollected: closing.fee_collected ?? null } : null,
        fee: canViewFinancials ? (feeDetail.data ? { projectedFee: feeDetail.data.projectedFee, successFee: feeDetail.data.successFee } : null) : undefined,
      })}
    />
  );

  const notice = isOnHold || isClosed ? (
    <>
      {isOnHold && (
        <HoldBanner
          holdDate={company.holdDate}
          reason={company.holdReason}
          revisitOn={company.holdRevisitDate}
          today={today}
          onReactivate={onReactivate}
          pending={reactivate.isPending}
        />
      )}
      {isClosed && (
        <ClosedSummary closing={closing ?? null} onEdit={() => setCloseDealOpen(true)} onReopen={handleReopen} reopening={reopen.isPending} />
      )}
    </>
  ) : undefined;

  const tabs = (
    <Tabs value={tab} onValueChange={(v) => setTab(v as ClientTab)}>
      <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b bg-transparent p-0">
        <TabsTrigger value="overview" className={TAB}>Overview</TabsTrigger>
        <TabsTrigger value="pipeline" className={TAB}>
          Go-to-Market
          {trackerTotal > 0 && <Badge variant="secondary" className="ml-1.5 px-1.5 text-[10px]">{trackerTotal}</Badge>}
        </TabsTrigger>
        <TabsTrigger value="documents" className={TAB}>Documents</TabsTrigger>
      </TabsList>
    </Tabs>
  );

  const view = tab === "pipeline" ? (
    <section className="space-y-4">
      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-200">Go-to-Market Tracker</h2>
      {tracker.data && <TrackerHeadline aggregates={tracker.data.aggregates} />}
      <BuyerTrackerGrid clientId={company.id} onAddClick={() => setAddBuyersOpen(true)} />
      <AddBuyersDialog clientId={company.id} open={addBuyersOpen} onOpenChange={setAddBuyersOpen} />
    </section>
  ) : tab === "documents" ? (
    <DocumentManager companyId={company.id} />
  ) : undefined;

  const work = (
    <>
      <BriefPanel
        summary={summary.data}
        loading={summary.isLoading}
        error={summary.isError}
        onRetry={() => summary.refetch()}
        openLabel={`Open on ${company.name}`}
        target={{ companyId: company.id }}
        nextStepOpen={nextStepOpen}
        onNextStepOpenChange={setNextStepOpen}
      />
      <ProcessPanel
        companyId={company.id}
        project={project.data}
        loading={project.isLoading}
        error={project.isError}
        onRetry={() => project.refetch()}
        funnel={tracker.data?.aggregates ?? null}
        today={today}
        onOpenTracker={() => setTab("pipeline")}
        onCreateProject={() => setProjectWizardOpen(true)}
        onLinkProject={() => setLinkProjectOpen(true)}
        onChangeProject={() => setChangeProjectOpen(true)}
      />
      <DealSnapshotPanel
        company={company}
        saveField={saveField}
        saveFieldAsync={saveFieldAsync}
        lines={lines}
        linesLoading={offers.isLoading}
        today={today}
        fee={canViewFinancials ? feeDetail : undefined}
        onOpenTracker={() => setTab("pipeline")}
      />
      <EditableTextPanel title="About" value={company.description} onSave={(v) => saveFieldAsync("description", v)} emptyText="No description yet." />
      <RecordPanel id="record-activity">
        <ActivityTimeline
          scope={{ kind: "company", companyId: company.id, companyName: company.name, contacts: people }}
          compose={compose}
          onComposeChange={setCompose}
        />
      </RecordPanel>
    </>
  );

  const details: [string, ReactNode][] = [
    ["Engagement", <InlineEdit key="engagement" label="Engagement type" value={company.engagementType} type="select" options={ENGAGEMENT_TYPES} addLabel="Add" onSave={(v) => saveFieldAsync("engagementType", v)} />],
    ["Employees", <InlineEdit key="employees" label="Employees" value={company.employeeCount} placeholder="e.g. 85" addLabel="Add" onSave={(v) => saveFieldAsync("employeeCount", v)} />],
    ...(company.daysInMarket != null && company.daysInMarket > 0 ? [["In market", `${company.daysInMarket} days`] as [string, ReactNode]] : []),
    ["Added", fullDay(company.createdAt)],
    ["Entered via", sourceLabel(company.creationSource)],
    ["Added by", company.creator?.name ?? "Unknown"],
  ];

  const rail = (
    <CompanyRail
      company={company}
      summary={summary}
      details={details}
      detailsExtra={<div className="mt-2.5 border-t pt-2.5"><SourceCard companyId={company.id} compact /></div>}
      onSaveNotes={(v) => saveFieldAsync("notes", v)}
      onAddPerson={() => setAddPersonOpen(true)}
    />
  );

  return (
    <AppLayout title={company.name}>
      <RecordFrame header={header} facts={facts} notice={notice} tabs={tabs} view={view} work={work} rail={rail} />

      <CloseDealDialog
        companyId={company.id}
        companyName={company.name}
        defaultEbitda={company.ebitda}
        expectedFeePct={feeDetail.data?.feePct ?? null}
        creditsDrawn={feeDetail.data ? Math.round(feeDetail.data.workfeeAccrued + feeDetail.data.entryCredits) : 0}
        existing={closing ?? null}
        open={closeDealOpen}
        onOpenChange={setCloseDealOpen}
      />
      <HoldClientDialog companyId={company.id} open={holdOpen} onOpenChange={setHoldOpen} />
      <AddContactModal open={addPersonOpen} onOpenChange={setAddPersonOpen} preselectedCompanyId={company.id} preselectedCompanyName={company.name} />
      <ProjectWizard
        open={projectWizardOpen}
        onOpenChange={setProjectWizardOpen}
        preselectedCompanyId={company.id}
        preselectedCompanyName={company.name}
        preselectedDealType={company.engagementType || undefined}
        onSuccess={() => { queryClient.invalidateQueries({ queryKey: ["client-project", company.id] }); }}
      />
      <LinkProjectDialog clientId={company.id} clientName={company.name || "this client"} open={linkProjectOpen} onOpenChange={setLinkProjectOpen} />
      {project.data && (
        <LinkProjectDialog
          clientId={company.id}
          clientName={company.name || "this client"}
          open={changeProjectOpen}
          onOpenChange={setChangeProjectOpen}
          replaceProjectId={project.data.id}
        />
      )}
      <ArchiveCompanyDialog company={company} open={archiveOpen} onOpenChange={setArchiveOpen} />
    </AppLayout>
  );
}
